import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Chrome, Download, FileText, Monitor, RefreshCw, Trash2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { downloadHelperPackage } from "../api";
import { useHelperLogs, useHelperMutations, useHelperOverview } from "../hooks";
import type { HelperInstance, HelperSettings } from "../types";

// Zakładka „Wtyczki" — panel dla developera. Wtyczka „ELGW5 KW Helper" chodzi po EKW
// w przeglądarce w biurze (portal odbija serwerownię i tryb bezgłowy), więc bez tej
// strony nikt nie wie, gdzie ona właściwie jest, w jakiej wersji i co robiła w nocy.

const fmtDateTime = (v: string | number | null | undefined) =>
  v ? new Date(v).toLocaleString("pl-PL") : "—";

function sinceLabel(iso: string | null): { text: string; tone: "green" | "yellow" | "red" } {
  if (!iso) return { text: "nigdy", tone: "red" };
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 15) return { text: `${Math.max(0, Math.round(mins))} min temu`, tone: "green" };
  if (mins < 24 * 60) return { text: `${Math.round(mins / 60)} godz. temu`, tone: "yellow" };
  return { text: `${Math.round(mins / 1440)} dni temu`, tone: "red" };
}

/** Jednym zdaniem: co ta wtyczka teraz robi (albo dlaczego nie robi nic). */
function stateLabel(inst: HelperInstance): { text: string; tone: "green" | "yellow" | "red" } {
  const st = inst.state || {};
  const s = inst.settings || {};
  if (st.running) return { text: "pracuje nad księgą", tone: "green" };
  if (st.pausedUntil && st.pausedUntil > Date.now())
    return { text: `zatrzymana: ${st.pauseReason || "bez powodu"}`, tone: "red" };
  if (!s.enabled) return { text: "automat wyłączony", tone: "yellow" };
  if (st.nextRunAt && st.nextRunAt > Date.now())
    return { text: `następna księga za ${Math.ceil((st.nextRunAt - Date.now()) / 60000)} min`, tone: "green" };
  return { text: "czuwa", tone: "green" };
}

const OUTCOME_TONE: Record<string, "green" | "red" | "yellow"> = {
  ok: "green", blocked: "red", captcha_failed: "yellow", not_found: "yellow", error: "red",
};

function Tile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3">
      <div className="text-[11px] text-[var(--color-text-muted)]">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
      {hint && <div className="text-[11px] text-[var(--color-text-muted)]">{hint}</div>}
    </div>
  );
}

/** Zdalne ustawienia jednego stanowiska — wtyczka zabierze je przy najbliższym meldunku. */
function RemoteSettings({ inst }: { inst: HelperInstance }) {
  const m = useHelperMutations();
  const s = inst.settings || {};
  const [draft, setDraft] = useState<Partial<HelperSettings>>({
    enabled: !!s.enabled,
    intervalMin: s.intervalMin ?? 5,
    allDay: !!s.allDay,
    windowFrom: s.windowFrom ?? "22:00",
    windowTo: s.windowTo ?? "06:00",
    dailyMax: s.dailyMax ?? 200,
    ownWindow: s.ownWindow ?? true,
  });
  const set = (patch: Partial<HelperSettings>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div className="space-y-2 rounded-lg bg-[var(--color-surface-muted)] p-3 text-xs">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!draft.enabled}
            onChange={(e) => set({ enabled: e.target.checked })} />
          Automat włączony
        </label>
        <label className="flex items-center gap-1.5">
          Przerwa
          <input type="number" min={2} max={120} className="input-base w-16"
            value={draft.intervalMin} onChange={(e) => set({ intervalMin: Number(e.target.value) })} />
          min
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!draft.allDay}
            onChange={(e) => set({ allDay: e.target.checked })} />
          O każdej porze
        </label>
        <label className="flex items-center gap-1.5">
          Okno
          <input type="time" className="input-base" value={draft.windowFrom}
            onChange={(e) => set({ windowFrom: e.target.value })} />
          –
          <input type="time" className="input-base" value={draft.windowTo}
            onChange={(e) => set({ windowTo: e.target.value })} />
        </label>
        <label className="flex items-center gap-1.5">
          Limit/dobę
          <input type="number" min={1} max={500} className="input-base w-20"
            value={draft.dailyMax} onChange={(e) => set({ dailyMax: Number(e.target.value) })} />
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!draft.ownWindow}
            onChange={(e) => set({ ownWindow: e.target.checked })} />
          Osobne okno
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" loading={m.update.isPending}
          onClick={() => m.update.mutate({ id: inst.id, desired: draft })}>
          Zastosuj na stanowisku
        </Button>
        {!!Object.keys(inst.pending_desired || {}).length && (
          <span className="text-[11px] text-amber-600">
            Czeka na odbiór przez wtyczkę (do ~5 min).
          </span>
        )}
      </div>
    </div>
  );
}

function InstanceRow({ inst }: { inst: HelperInstance }) {
  const m = useHelperMutations();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(inst.label);
  const seen = sinceLabel(inst.last_seen_at);
  const state = stateLabel(inst);

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Monitor size={16} className="text-[var(--color-text-muted)]" />
        <input
          className="input-base w-52 text-sm"
          value={label}
          placeholder="Nazwa stanowiska (np. Recepcja — Dell)"
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => label !== inst.label && m.update.mutate({ id: inst.id, label })}
        />
        <Badge color={seen.tone}>meldunek {seen.text}</Badge>
        <Badge color={state.tone}>{state.text}</Badge>
        <span className="text-xs text-[var(--color-text-muted)]">
          {inst.browser || "?"} · {inst.platform || "?"} · wtyczka {inst.version || "?"}
          {inst.user ? ` · ${inst.user}` : ""}
        </span>
        <span className="text-xs">
          7 dni: <b className="text-emerald-600">{inst.week.ok}</b> ksiąg
          {inst.week.failed > 0 && <> · <b className="text-red-600">{inst.week.failed}</b> nieudanych</>}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs" title="Wtyczka zacznie zapisywać także kroki pośrednie — do diagnozy, nie na stałe">
            <input type="checkbox" checked={inst.log_verbose}
              onChange={(e) => m.update.mutate({ id: inst.id, log_verbose: e.target.checked })} />
            Zapisuj logi
          </label>
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? "Zwiń" : "Ustawienia"}
          </Button>
          <Button size="sm" variant="ghost" title="Zdejmij stanowisko z listy"
            onClick={() => m.remove.mutate(inst.id)}>
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-[var(--color-text-muted)]">
        ID instalacji: {inst.install_id} · pierwszy meldunek {fmtDateTime(inst.created_at)}
        {inst.state?.lastResult ? ` · ostatnio: ${inst.state.lastResult} (${fmtDateTime(inst.state.lastAt)})` : ""}
      </div>
      {open && <RemoteSettings inst={inst} />}
    </div>
  );
}

function InstallGuide({ version }: { version: string }) {
  const [busy, setBusy] = useState<"chrome" | "firefox" | null>(null);
  const grab = async (flavor: "chrome" | "firefox") => {
    setBusy(flavor);
    try { await downloadHelperPackage(flavor); } finally { setBusy(null); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-lg border border-[var(--color-border)] p-3 space-y-2">
        <div className="flex items-center gap-2 font-medium"><Chrome size={16} /> Chrome / Edge — zalecane</div>
        <ol className="list-decimal pl-4 text-xs space-y-1 text-[var(--color-text-secondary)]">
          <li>Pobierz i rozpakuj paczkę (powstanie katalog <code>ekw_helper</code>).</li>
          <li>Wejdź w <code>chrome://extensions</code> i włącz <b>Tryb dewelopera</b>.</li>
          <li>Kliknij <b>Załaduj rozpakowane</b> i wskaż katalog <code>ekw_helper</code>.</li>
          <li>Otwórz ELGW5 w tej przeglądarce i zaloguj się — wtyczka bierze token z aplikacji.</li>
          <li>Ikona wtyczki → ustaw okno nocne i włącz automat. Stanowisko pojawi się na liście niżej.</li>
        </ol>
        <Button size="sm" loading={busy === "chrome"} onClick={() => grab("chrome")}>
          <Download size={14} /> Pobierz wtyczkę (Chrome/Edge) {version}
        </Button>
      </div>
      <div className="rounded-lg border border-[var(--color-border)] p-3 space-y-2">
        <div className="flex items-center gap-2 font-medium"><FileText size={16} /> Firefox</div>
        <ol className="list-decimal pl-4 text-xs space-y-1 text-[var(--color-text-secondary)]">
          <li>Pobierz i rozpakuj paczkę firefoksową (ma już właściwy <code>manifest.json</code>).</li>
          <li>Wejdź w <code>about:debugging#/runtime/this-firefox</code>.</li>
          <li><b>Wczytaj dodatek tymczasowy</b> → wskaż plik <code>manifest.json</code>.</li>
          <li>Zaloguj się w ELGW5 w tej przeglądarce.</li>
        </ol>
        <p className="text-[11px] text-amber-600">
          Dodatek tymczasowy znika po zamknięciu Firefoksa — na stanowisko nocne lepszy jest
          Chrome/Edge (albo podpisany XPI z konta na addons.mozilla.org).
        </p>
        <Button size="sm" variant="secondary" loading={busy === "firefox"} onClick={() => grab("firefox")}>
          <Download size={14} /> Pobierz wtyczkę (Firefox) {version}
        </Button>
      </div>
    </div>
  );
}

function LogTable({ installId }: { installId: string }) {
  const [verbose, setVerbose] = useState(false);
  const { data, isLoading, refetch, isFetching } = useHelperLogs({ installId, verbose, limit: 100 });
  const items = data?.items ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={verbose} onChange={(e) => setVerbose(e.target.checked)} />
          Pokaż też kroki pośrednie (gdy stanowisko ma włączone „Zapisuj logi")
        </label>
        <Button size="sm" variant="ghost" onClick={() => refetch()} loading={isFetching}>
          <RefreshCw size={14} /> Odśwież
        </Button>
      </div>
      {isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          Brak wpisów. Automat zapisuje tu każdą próbę pobrania księgi — a przy zatrzymaniu
          także zrzut ekranu z portalu.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[var(--color-text-muted)]">
              <tr>
                <th className="py-1 pr-3">Kiedy</th>
                <th className="py-1 pr-3">Księga</th>
                <th className="py-1 pr-3">Etap</th>
                <th className="py-1 pr-3">Wynik</th>
                <th className="py-1 pr-3">Szczegóły</th>
                <th className="py-1 pr-3">Koszt</th>
                <th className="py-1">Zrzut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-[var(--color-border)] align-top">
                  <td className="py-1 pr-3 whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {row.kw_number || "—"}
                    {row.parcel_teryt && (
                      <div className="text-[10px] text-[var(--color-text-muted)]">dz. {row.parcel_teryt}</div>
                    )}
                  </td>
                  <td className="py-1 pr-3">{row.stage_label}</td>
                  <td className="py-1 pr-3">
                    <Badge color={OUTCOME_TONE[row.outcome] ?? "gray"}>{row.outcome_label}</Badge>
                  </td>
                  <td className="py-1 pr-3 max-w-[420px] whitespace-pre-wrap">{row.detail}</td>
                  <td className="py-1 pr-3 whitespace-nowrap">{row.cost_usd ? `$${row.cost_usd}` : ""}</td>
                  <td className="py-1">
                    {row.screenshot_url ? (
                      <a className="text-[var(--color-primary)] hover:underline"
                        href={row.screenshot_url} target="_blank" rel="noreferrer">
                        zobacz
                      </a>
                    ) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ExtensionsPage() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, error } = useHelperOverview();
  const [logFor, setLogFor] = useState("");

  if (!user?.is_developer && !user?.has_browser_extensions_permission)
    return <Navigate to="/" replace />;
  if (isLoading) return <Spinner />;
  // Bez tego błąd odpytania zostawiał na stronie wieczny spinner i nikt nie wiedział dlaczego.
  if (error || !data)
    return (
      <p className="p-4 text-sm text-red-600">
        Nie udało się pobrać stanu wtyczek: {(error as Error)?.message || "brak odpowiedzi z serwera"}.
      </p>
    );

  const q = data.queue;
  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-lg font-semibold">Wtyczki przeglądarkowe</h1>
        <p className="text-xs text-[var(--color-text-secondary)] max-w-3xl">
          <b>ELGW5 KW Helper</b> pobiera treść ksiąg wieczystych z portalu EKW. Musi działać
          w przeglądarce w biurze: portal odbija zarówno adres serwerowni, jak i tryb bezgłowy
          (zmierzone — klient HTTP i Chrome headless dostają 403, realne okno 200). Backend ELGW5
          nigdy nie łączy się z EKW; dostaje wyłącznie treść, którą i tak wyświetliłby człowiek.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tile label="Ksiąg w kolejce" value={q.pending_kw} hint={`${q.pending_parcels} działek`} />
        <Tile label="Pobrane (łącznie)" value={q.done_parcels} hint="działek z treścią KW" />
        <Tile label="Dziś udane" value={q.today_ok} />
        <Tile label="Dziś nieudane" value={q.today_failed} />
        <Tile label="Koszt dziś" value={`$${q.today_cost_usd}`} hint="odczyt kodu z obrazka + wizja" />
      </div>
      {q.invalid_kw_parcels > 0 && (
        <p className="text-xs text-amber-600">
          {q.invalid_kw_parcels} działek ma w polu KW wpis, który księgą nie jest (numer decyzji,
          adnotacja) — automat ich nie tknie, trzeba je poprawić ręcznie.
        </p>
      )}

      <section className="space-y-2">
        <h2 className="font-medium">Instalacja</h2>
        {data.package.available ? (
          <InstallGuide version={data.package.version} />
        ) : (
          <p className="text-xs text-red-600">
            Brak plików wtyczki na serwerze ({data.package.path}) — paczki nie ma z czego zbudować.
          </p>
        )}
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Warunki pracy nocnej: komputer włączony, przeglądarka otwarta, w niej zalogowana karta
          ELGW5 (wtyczka celowo nie odświeża tokenu sama — rotacja refresh-tokenu wylogowałaby
          człowieka z aplikacji). Token żyje 10 godzin, więc wystarczy wejść do ELGW5 wieczorem.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium">Stanowiska ({data.instances.length})</h2>
        {data.instances.length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">
            Żadne stanowisko się jeszcze nie zameldowało. Stanowisko pojawia się tu samo,
            gdy wtyczka pierwszy raz połączy się z ELGW5.
          </p>
        ) : (
          <div className="space-y-2">
            {data.instances.map((inst) => (
              <div key={inst.id} className="space-y-2">
                <InstanceRow inst={inst} />
                <div className="pl-3">
                  <button className="text-[11px] text-[var(--color-primary)] hover:underline"
                    onClick={() => setLogFor(logFor === inst.install_id ? "" : inst.install_id)}>
                    {logFor === inst.install_id ? "Ukryj dziennik tego stanowiska" : "Dziennik tego stanowiska"}
                  </button>
                  {logFor === inst.install_id && (
                    <div className="mt-2"><LogTable installId={inst.install_id} /></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="font-medium">Dziennik — wszystkie stanowiska</h2>
        <LogTable installId="" />
      </section>
    </div>
  );
}
